import { useForm, UseFormReturn } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import cloneDeep from 'lodash/cloneDeep';
import { useHub } from 'src/state/redux/selector/hub';
import {
  HubSection,
  HubSectionType,
  HubSectionItemStatus,
  HubSharedFileContentType,
  TodoStatus,
  HubSectionStatus,
  NotationType,
} from '@alucio/aws-beacon-amplify/src/models';
import { HubORM } from 'src/types/orms';
import { URL_REGEX } from '@alucio/core';

// [TODO-DEBT] - Consider using https://github.com/fabien0102/ts-to-zod
//             - May automatically do the conversion from TS to Zod Schema for us
//               Since Zod only does Zod -> TS

const widgetTypeFormSchema = z.union([
  z.nativeEnum(HubSectionType),
  z.literal('TEXTAREA'),
  z.literal('SHARED_FILES'),
  z.literal('TODO_LIST'),
  z.literal('LINKS')
])

const boilerplate = {
  id: z.string(),
  sectionHeader: z.string()
    .min(1, { message: 'This field is required ' })
    .max(150),
  status: z.union([
    z.nativeEnum(HubSectionStatus),
    z.literal('ACTIVE'),
    z.literal('DELETED'),
  ]),
  type: widgetTypeFormSchema,
  visible: z.boolean(),
  locked: z.any(),
  order: z.number().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

const textareaFormSchema = {
  content: z.string().optional(),
}

const notationFormSchema = {
  id: z.string(),
  type: z.union([
    z.nativeEnum(NotationType),
    z.literal('CALLOUT'),
    z.literal('PAGE_NOTE'),
  ]),
  description: z.string().nullable().optional(),
  status: z.union([
    z.nativeEnum(HubSectionItemStatus),
    z.literal('ACTIVE'),
    z.literal('DELETED'),
  ]),
  pageId: z.string(),
  coordinate: z.array(z.object({
    x: z.number(),
    y: z.number(),
  })).nullable().optional(),
  createdAt: z.string().datetime(),
  createdBy: z.string(),
  updatedAt: z.string().datetime(),
  updatedBy: z.string(),
}

const sharedFilesFormSchema = {
  id: z.string(),
  contentId: z.string(),
  title: z.string(),
  contentType: z.union([
    z.nativeEnum(HubSharedFileContentType),
    z.literal('DOCUMENT_VERSION'),
  ]),
  documentVersionSettings: z.object({
    associatedFiles: z.array(z.object({
      associatedFileId: z.string(),
      versionId: z.string().nullable().optional(),
      notation: z.array(z.object({ ...notationFormSchema })).nullable().optional(),
    })).nullable().optional()
  }).nullable().optional(),
  status: z.union([
    z.nativeEnum(HubSectionItemStatus),
    z.literal('ACTIVE'),
    z.literal('DELETED'),
  ]),
  order: z.number().nonnegative().nullable().optional(),
  renderInline: z.boolean().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  pageSettings: z.array(z.object({
    pageId: z.string(),
    hidden: z.boolean().optional(),
  })).optional(),
  notation: z.array(z.object({ ...notationFormSchema })).nullable().optional(),
}

const toDosFormSchema = {
  id: z.string(),
  title: z
    .string()
    .min(1, { message: 'This field is required ' })
    .max(150),
  resolution: z
    .string()
    .max(500)
    .optional(),
  status: z.union([
    z.nativeEnum(TodoStatus),
    z.literal('ACTIVE'),
    z.literal('COMPLETED'),
    z.literal('DELETED'),
  ]),
  order: z.number().nonnegative(),
  completedAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

const linksFormSchema = {
  id: z.string(),
  url: z
    .string()
    .min(1, { message: 'This field is required ' })
    .refine((value) => {
      const url = value.startsWith('http://') || value.startsWith('https://') ? value : 'https://' + value;
      return URL_REGEX.test(url);
    }
    , { message: 'Invalid URL' }),
  title: z
    .string()
    .max(150)
    .optional(),
  status: z.union([
    z.nativeEnum(HubSectionItemStatus),
    z.literal('ACTIVE'),
    z.literal('DELETED'),
  ]),
  order: z.number().nonnegative(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
}

const widgetFormSchema = z.discriminatedUnion('type', [
  z.object({
    ...boilerplate,
    type: z.literal(HubSectionType.TEXTAREA),
    sectionHeader: z.string().max(150),
    textarea: z.object({ ...textareaFormSchema }),
  }),
  z.object({
    ...boilerplate,
    type: z.literal(HubSectionType.SHARED_FILES),
    sharedFiles: z.array(z.object({ ...sharedFilesFormSchema })).optional(),
  }),
  z.object({
    ...boilerplate,
    type: z.literal(HubSectionType.TODO_LIST),
    toDos: z.array(z.object({ ...toDosFormSchema })).optional(),
  }),
  z.object({
    ...boilerplate,
    type: z.literal(HubSectionType.LINKS),
    links: z.array(z.object({ ...linksFormSchema })).optional(),
  }),
]);

export const formSchema = z.object({
  name: z
    .string()
    .min(1, { message: 'This field is required' }),
  lockName: z.boolean(),
  widgets: z.record(widgetFormSchema),
});

interface useHubFormStateType {
  hubId: string
}

export type HubFormValue = {
  name: string,
  lockName: boolean,
  widgets: Record<string, HubSection>,
}

export type EditHubRHForm = UseFormReturn<HubFormValue>
export type WidgetFormName = `widgets.${string}`

export const getWidgetFormName = (widgetId: string): WidgetFormName => `widgets.${widgetId}`

/** Build form defaults for every active section, keyed by section id. */
export function getDefaultHubFormValue (hubORM?: HubORM): HubFormValue {
  const activeWidgets = hubORM?.model.hubSections
    ?.filter(widget => widget.status === HubSectionStatus.ACTIVE) ?? [];

  const widgets = activeWidgets.reduce<Record<string, HubSection>>((result, widget) => {
    if (widget.type === HubSectionType.SHARED_FILES) {
      const sharedFiles = widget.sharedFiles?.map(sharedFile => {
        const associatedFiles = sharedFile.documentVersionSettings?.associatedFiles?.map(associatedFile => ({
          ...associatedFile,
          versionId: associatedFile.versionId || undefined,
          notation: associatedFile.notation?.map(notation => ({
            ...notation,
            description: notation.description || undefined,
          })),
        }));
        return {
          ...sharedFile,
          documentVersionSettings: associatedFiles ? { associatedFiles } : undefined,
          pageSettings: sharedFile.pageSettings || undefined,
          notation: sharedFile.notation?.map(notation => ({
            ...notation,
            description: notation.description || undefined,
          })) || undefined,
        };
      });
      result[widget.id] = { ...widget, sharedFiles };
    } else if (widget.type === HubSectionType.TODO_LIST) {
      result[widget.id] = {
        ...widget,
        toDos: widget.toDos?.map(toDo => ({
          ...toDo,
          completedAt: toDo.completedAt || undefined,
        })),
      };
    } else if (widget.type === HubSectionType.LINKS) {
      result[widget.id] = { ...widget, links: widget.links || [] };
    } else {
      result[widget.id] = widget;
    }
    return result;
  }, {});

  const defaultHubFormValue = {
    name: hubORM?.model.name || '',
    lockName: hubORM?.model.lockName || false,
    widgets,
  };

  return cloneDeep(defaultHubFormValue)
}

const useHubFormState = (props: useHubFormStateType): EditHubRHForm => {
  const { hubId } = props
  const hubORM = useHub(hubId)
  const defaultValues = getDefaultHubFormValue(hubORM)

  const rhForm = useForm<HubFormValue>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues,
  })

  return rhForm
}

export default useHubFormState
